import { useState, useEffect } from 'react';
import { HelpTip, Loading, PANEL_HELP, PanelHowTo, api, useToast } from './_barrel.js';


        function BusinessHostileWatch({ token, businessId }) {
            const [mentions, setMentions] = useState(null);
            const [running, setRunning] = useState(false);
            const [reclassifying, setReclassifying] = useState(false);
            const [lastResult, setLastResult] = useState(null);
            const [error, setError] = useState(null);
            const [refresh, setRefresh] = useState(0);
            const [expanded, setExpanded] = useState(false);
            const [expandedOff, setExpandedOff] = useState(false);
            const [showAllTime, setShowAllTime] = useState(false);
            const toast = useToast();

            useEffect(() => {
                (async () => {
                    try {
                        const r = await api(`/api/v1/businesses/${businessId}/mentions?limit=500`, {}, token);
                        setMentions(r || []);
                    } catch (e) { setError(e.message); }
                })();
            }, [token, businessId, refresh]);

            const runGoogleNews = async () => {
                if (!confirm('Run Google News collector against this business? Roughly 1-2p in LLM costs per 25 new results.')) return;
                setRunning(true); setError(null); setLastResult(null);
                try {
                    const r = await api(`/api/v1/businesses/${businessId}/collectors/google-news`, { method: 'POST' }, token);
                    setLastResult(r);
                    if (r.new_mentions > 0) {
                        toast(`${r.new_mentions} new mention(s)`);
                    } else {
                        toast(r.note || 'No new mentions.');
                    }
                    setRefresh(x => x+1);
                } catch (e) { setError(e.message); }
                finally { setRunning(false); }
            };

            const runReclassify = async () => {
                if (!confirm('Re-run the AI classifier on all existing mentions for this business?')) return;
                setReclassifying(true); setError(null); setLastResult(null);
                try {
                    const r = await api(`/api/v1/businesses/${businessId}/mentions/reclassify`, { method: 'POST' }, token);
                    setLastResult(r);
                    toast(`Reclassified ${r.classified} mention(s)`);
                    setRefresh(x => x+1);
                } catch (e) { setError(e.message); }
                finally { setReclassifying(false); }
            };

            if (mentions === null) {
                return (
                    <div className="panel mb-6">
                        <div className="panel-header"><div className="font-display text-xl gold">Hostile Mention Watch<HelpTip text={PANEL_HELP.hostile_mention_watch} /></div></div>
                        <div className="px-6 py-4 text-sm text-muted">Loading…</div>
                    </div>
                );
            }

            // Recency window: neutral/off-target/unclassified mentions older than
            // 30 days (dated by the article/post's own publish date, falling back
            // to first-seen) are hidden by default so old items a collector
            // resurfaces don't clutter the live watch. Hostile mentions are never
            // hidden by this window — a genuine finding must not look like "nothing
            // found" just because it's old; age-filtering a real hostile hit was a bug.
            const MENTION_WINDOW_DAYS = 30;
            const _winCutoff = Date.now() - MENTION_WINDOW_DAYS * 24 * 60 * 60 * 1000;
            const isHostile = c => c && c.startsWith('hostile_');
            const withinWindow = (m) => {
                if (isHostile(m.llm_classification)) return true;
                const d = m.published_at || m.first_seen_at;
                const t = d ? new Date(d).getTime() : NaN;
                return isNaN(t) ? true : t >= _winCutoff;
            };
            const _allMentions = mentions;
            const winMentions = showAllTime ? _allMentions : _allMentions.filter(withinWindow);
            const staleHidden = _allMentions.length - _allMentions.filter(withinWindow).length;

            // Group by class
            const hostileMentions = winMentions.filter(m => isHostile(m.llm_classification));
            const neutralMentions = winMentions.filter(m => m.llm_classification && !isHostile(m.llm_classification) && m.llm_classification !== 'off_target');
            const offTarget = winMentions.filter(m => m.llm_classification === 'off_target');
            const unclassified = winMentions.filter(m => !m.llm_classification);

            const classLabel = c => ({
                hostile_legal:       'Hostile (legal)',
                hostile_misconduct:  'Hostile (misconduct)',
                hostile_reputation:  'Hostile (reputation)',
                hostile_financial:   'Hostile (financial)',
                neutral_news:        'Neutral news',
                neutral_mention:     'Neutral mention',
                off_target:          'Off-target',
            })[c] || c || 'Unclassified';

            const classBand = c => {
                if (!c) return 'low';
                if (c.startsWith('hostile_')) return c === 'hostile_legal' || c === 'hostile_misconduct' ? 'high' : 'moderate';
                return 'low';
            };

            const renderMention = (m) => (
                <div key={m.id} className="table-row px-6 py-3">
                    <div className="flex justify-between items-start gap-4">
                        <div className="flex-1 min-w-0">
                            <div className="text-sm">
                                {(() => {
                                    const u = String(m.url || '').trim();
                                    const ok = /^https?:\/\//i.test(u);
                                    return ok ? (
                                        <a href={u} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--gold)]"
                                           onClick={(e) => { e.preventDefault(); window.open(u, '_blank', 'noopener,noreferrer'); }}>
                                            {m.title || '(no title)'}
                                        </a>
                                    ) : (
                                        <span title={u || 'no link on file'}>{m.title || '(no title)'}</span>
                                    );
                                })()}
                            </div>
                            <div className="text-xs text-muted font-mono mt-1">
                                {m.source}
                                {m.published_at && ` · ${new Date(m.published_at).toLocaleDateString('en-GB', {day:'numeric', month:'short', year:'numeric'})}`}
                            </div>
                            {m.llm_reasoning && (
                                <div className="text-xs text-muted mt-1 italic">{m.llm_reasoning}</div>
                            )}
                        </div>
                        {m.llm_classification && (
                            <span className={`severity-pill band-${classBand(m.llm_classification)}`}>
                                {classLabel(m.llm_classification)}
                            </span>
                        )}
                    </div>
                </div>
            );

            return (
                <div className="panel mb-6">
                    <div className="panel-header flex justify-between items-center">
                        <div>
                            <div className="font-display text-xl gold">Hostile Mention Watch<HelpTip text={PANEL_HELP.hostile_mention_watch} /></div>
                            <div className="text-xs text-muted font-mono mt-1">
                                News (incl. GDELT &amp; Gazette) scanned + AI-classified · every result shown, incl. off-target
                            </div>
                        </div>
                        <div className="flex gap-2">
                            {mentions.length > 0 && (
                                <button className="btn-ghost" onClick={runReclassify} disabled={reclassifying || running}>
                                    {reclassifying ? 'Reclassifying…' : 'Reclassify with AI'}
                                </button>
                            )}
                            <button className="btn-gold" onClick={runGoogleNews} disabled={running || reclassifying}>
                                {running ? 'Running…' : 'Run Google News'}
                            </button>
                        </div>
                    </div>
                    <PanelHowTo id="business_hostile" />

                    {error && <div className="px-6 py-3 text-sm" style={{color:'var(--red)'}}>{error}</div>}

                    {lastResult && (
                        <div className="mx-6 mt-4 p-3" style={{background:'rgba(201,169,110,0.05)', border:'1px solid rgba(201,169,110,0.15)'}}>
                            <div className="text-xs font-mono text-muted mb-2">LAST RUN</div>
                            <div className="text-sm mb-2">
                                {lastResult.new_mentions} new · {lastResult.duplicates_skipped} duplicates skipped · {lastResult.classified} classified
                            </div>
                            {lastResult.note && (
                                <div className="text-xs text-muted font-mono">{lastResult.note}</div>
                            )}
                        </div>
                    )}

                    {mentions.length === 0 ? (
                        <div className="px-6 py-6 text-sm text-muted">
                            No mentions collected yet. Click "Run Google News" to scan recent press coverage.
                        </div>
                    ) : winMentions.length === 0 ? (
                        <div className="px-6 py-6 text-sm text-muted">
                            No mentions in the last {MENTION_WINDOW_DAYS} days. {staleHidden} older mention(s) on file are kept out of the live watch.
                        </div>
                    ) : (
                        <div className="mt-2">
                            {staleHidden > 0 && (
                                <div className="px-6 pt-3 text-xs text-muted flex items-center gap-2"
                                     title={`Only mentions from the last ${MENTION_WINDOW_DAYS} days are shown by default, dated by when the article or post was published. Older items stay on file.`}>
                                    {showAllTime
                                        ? <span>Showing all {_allMentions.length} on file · <button onClick={() => setShowAllTime(false)} style={{color:'var(--gold)', background:'transparent', border:'none', cursor:'pointer', textDecoration:'underline', padding:0, font:'inherit'}}>last {MENTION_WINDOW_DAYS} days only</button></span>
                                        : <span>Showing last {MENTION_WINDOW_DAYS} days · <button onClick={() => setShowAllTime(true)} style={{color:'var(--gold)', background:'transparent', border:'none', cursor:'pointer', textDecoration:'underline', padding:0, font:'inherit'}}>show all {staleHidden} older</button></span>}
                                </div>
                            )}
                            {hostileMentions.length > 0 && (
                                <div>
                                    <div className="px-6 py-3 font-mono text-xs uppercase tracking-widest" style={{color:'var(--amber)'}}>
                                        Hostile ({hostileMentions.length})
                                    </div>
                                    {hostileMentions.map(renderMention)}
                                </div>
                            )}
                            {(neutralMentions.length > 0 || unclassified.length > 0) && (
                                <div>
                                    <div className="px-6 py-3 font-mono text-xs uppercase tracking-widest text-muted flex justify-between cursor-pointer"
                                         onClick={() => setExpanded(!expanded)}>
                                        <span>Neutral / Unclassified ({neutralMentions.length + unclassified.length})</span>
                                        <span>{expanded ? '−' : '+'}</span>
                                    </div>
                                    {expanded && (
                                        <div>
                                            {neutralMentions.map(renderMention)}
                                            {unclassified.map(renderMention)}
                                        </div>
                                    )}
                                </div>
                            )}
                            {offTarget.length > 0 && (
                                <div>
                                    <div className="px-6 py-3 font-mono text-xs uppercase tracking-widest text-muted flex justify-between cursor-pointer"
                                         onClick={() => setExpandedOff(!expandedOff)}
                                         title="Results the AI judged not to be about this company. Shown so you can verify — reclassify any it got wrong.">
                                        <span>Off-target ({offTarget.length})</span>
                                        <span>{expandedOff ? '−' : '+'}</span>
                                    </div>
                                    {expandedOff && (
                                        <div style={{ opacity: 0.75 }}>
                                            {offTarget.map(renderMention)}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        }

export { BusinessHostileWatch };
